import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GraphQLQuery } from "@aws-amplify/api";
import { generateClient } from "aws-amplify/api";
import { signOut } from "aws-amplify/auth";
import { usePostHog } from "@posthog/react";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import { closeModalWithResult } from "../../store/modalSlice";
import { updateUser } from "../../graphql/customMutations";
import { UpdateUserMutation, TermsAndConditionsStatusEnum } from "../../API";
import {
  parseAuditTrail,
  addAcceptance,
  hasOlderAcceptances,
  getCurrentTermsIssuedDate,
  getCurrentPrivacyPolicyIssuedDate,
} from "../../utils/termsAuditTrail";
import { buildInitialConsent, parseUserConsent } from "../../utils/userConsent";
import { getTermsOfService, getPrivacyPolicy } from "../../utils/documentLoader";
import ModalBase from "./ModalBase";
import DocumentViewerModal from "./DocumentViewerModal";

interface PlatformConsentModalProps {
  onClose: (result?: any) => void;
  userId?: string;
}

const client = generateClient();

const PlatformConsentModal: React.FC<PlatformConsentModalProps> = ({ onClose, userId }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const posthog = usePostHog();
  const { capture } = usePostHogCapture();

  const [legalAgreementsChecked, setLegalAgreementsChecked] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<"terms" | "privacy" | null>(null);

  const termsDoc = useMemo(() => getTermsOfService(), []);
  const privacyDoc = useMemo(() => getPrivacyPolicy(), []);

  const termsIssuedDate = getCurrentTermsIssuedDate();
  const privacyIssuedDate = getCurrentPrivacyPolicyIssuedDate();

  const existingTermsTrail = useMemo(
    () => parseAuditTrail(user?.permissionDetails?.termsAndConditionsAuditTrail),
    [user?.permissionDetails?.termsAndConditionsAuditTrail]
  );
  const existingPrivacyTrail = useMemo(
    () => parseAuditTrail(user?.permissionDetails?.privacyPolicyAuditTrail),
    [user?.permissionDetails?.privacyPolicyAuditTrail]
  );

  const isUpdatedTerms = useMemo(
    () => hasOlderAcceptances(existingTermsTrail, termsIssuedDate),
    [existingTermsTrail, termsIssuedDate]
  );
  const isUpdatedPrivacy = useMemo(
    () => hasOlderAcceptances(existingPrivacyTrail, privacyIssuedDate),
    [existingPrivacyTrail, privacyIssuedDate]
  );
  const isUpdated = isUpdatedTerms || isUpdatedPrivacy;

  // Pre-populate analytics consent from existing userConsent
  useEffect(() => {
    const existingConsent = parseUserConsent(user?.permissionDetails?.userConsent);
    setAnalyticsChecked(existingConsent.userTracking);
  }, [user?.permissionDetails?.userConsent]);

  const canAccept = legalAgreementsChecked;

  const handleLegalAgreementsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLegalAgreementsChecked(event.target.checked);
  };

  const handleAccept = useCallback(async () => {
    if (!userId || !canAccept) return;

    try {
      const termsAuditTrailJson = addAcceptance(existingTermsTrail, termsIssuedDate);
      const privacyAuditTrailJson = addAcceptance(existingPrivacyTrail, privacyIssuedDate);
      const userConsentJson = buildInitialConsent(analyticsChecked);

      const updateInput = {
        id: userId,
        termsAndConditionsAuditTrail: termsAuditTrailJson,
        privacyPolicyAuditTrail: privacyAuditTrailJson,
        userConsent: userConsentJson,
        // Legacy fields for backward compatibility
        termsAndConditionsReviewed: TermsAndConditionsStatusEnum.True,
        termsAndConditionsLocation: termsIssuedDate,
        chatPrivacyPolicyAccepted: TermsAndConditionsStatusEnum.True,
        chatPrivacyPolicyLocation: privacyIssuedDate,
      };

      const result = await client.graphql<GraphQLQuery<UpdateUserMutation>>({
        query: updateUser,
        variables: { input: updateInput },
      });

      // Fire separate events for each document
      capture(
        POSTHOG_EVENTS.TERMS_ACCEPTED,
        { terms_issued_date: termsIssuedDate, tracking_consent: analyticsChecked },
        { bypassConsent: true }
      );
      capture(
        POSTHOG_EVENTS.CHAT_PRIVACY_ACCEPTED,
        { privacy_issued_date: privacyIssuedDate },
        { bypassConsent: true }
      );

      dispatch(
        closeModalWithResult({
          success: true,
          modalType: "platformConsent",
          termsAuditTrailJson,
          privacyAuditTrailJson,
          userConsentJson,
          termsValue: "True",
          chatPrivacyValue: "True",
          userData: result.data?.updateUser,
        })
      );
    } catch (error) {
      console.error("Error updating platform consent:", error);
      dispatch(closeModalWithResult({ success: false, error }));
    }
  }, [
    userId,
    canAccept,
    dispatch,
    capture,
    existingTermsTrail,
    existingPrivacyTrail,
    termsIssuedDate,
    privacyIssuedDate,
    analyticsChecked,
  ]);

  const handleDecline = useCallback(async () => {
    try {
      capture(POSTHOG_EVENTS.TERMS_DECLINED, undefined, { bypassConsent: true });
      await signOut();
      posthog?.reset();

      dispatch(
        closeModalWithResult({
          success: false,
          modalType: "platformConsent",
          declined: true,
          loggedOut: true,
        })
      );
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
      dispatch(closeModalWithResult({ success: false, error }));
    }
  }, [dispatch, posthog, capture]);

  const footer = (
    <button
      type="button"
      onClick={handleAccept}
      disabled={!canAccept}
      className="eai-btn-grn py-2 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Accept
    </button>
  );

  return (
    <>
      <ModalBase
        title={isUpdated ? "Updated Platform Agreement" : "Platform Agreement"}
        onClose={handleDecline}
        size="lg"
        footer={footer}
      >
        {isUpdated && (
          <div className="mb-4 p-3 bg-eai-checklist-lt border-l-4 border-eai-primary-accent text-eai-primary-accent rounded-r-md">
            <p className="text-sm">
              Our platform agreements have been updated. Please review and accept the latest versions to continue.
            </p>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-6">
          Please review and agree to the following to access the platform.
        </p>

        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={legalAgreementsChecked}
              onChange={handleLegalAgreementsChange}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-eai-primary-accent text-eai-primary-accent focus:ring-eai-primary-accent"
            />
            <span className="text-sm text-gray-700">
              I have read and agree to the{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setViewingDocument("terms");
                }}
                className="text-eai-primary-accent underline hover:text-[#5e6a2f] font-medium"
              >
                Terms of Service
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setViewingDocument("privacy");
                }}
                className="text-eai-primary-accent underline hover:text-[#5e6a2f] font-medium"
              >
                Privacy Policy
              </button>
            </span>
          </label>

          {/* Usage Analytics */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={analyticsChecked}
              onChange={(e) => setAnalyticsChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-eai-primary-accent text-eai-primary-accent focus:ring-eai-primary-accent"
            />
            <div>
              <span className="text-sm text-gray-700">
                I consent to the collection of usage analytics to help improve the platform
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                Optional. You can change this at any time in Settings.
              </p>
            </div>
          </label>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          Accepting the checkbox above records your agreement to both documents and is required to access the platform.
        </p>
      </ModalBase>

      {/* Document viewer sub-modal */}
      <DocumentViewerModal
        open={viewingDocument === "terms"}
        title={termsDoc.title}
        content={termsDoc.content}
        onClose={() => setViewingDocument(null)}
      />
      <DocumentViewerModal
        open={viewingDocument === "privacy"}
        title={privacyDoc.title}
        content={privacyDoc.content}
        onClose={() => setViewingDocument(null)}
      />
    </>
  );
};

export default PlatformConsentModal;
